import { useCallback, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { APIProvider } from '@vis.gl/react-google-maps'
import { useNavigate } from 'react-router-dom'
import { RefreshCw } from 'lucide-react'
import { Button, Spinner } from '@/components/ds2'
import type { BoxTruckMapDriver } from '../services/boxTruckLoadsApi'
import type { DriverClusterInspection, DriverFocusRequest, StateFocusRequest } from '../types/boxTruckMap'
import { useBoxTruckMap } from '../hooks/useBoxTruckMap'
import { useBoxTruckMapStateLoads } from '../hooks/useBoxTruckMapStateLoads'
import { buildStateActivities, getStateCode, getStateName } from '../utils/boxTruckMapState'
import { BoxTruckMapActivityPanel } from '../components/BoxTruckMapActivityPanel'
import { BoxTruckMapCanvas } from '../components/BoxTruckMapCanvas'
import { BoxTruckMapLegend } from '../components/BoxTruckMapLegend'
import { BoxTruckMapToolbar } from '../components/BoxTruckMapToolbar'

const openDriverFindLoads = (driver: BoxTruckMapDriver): void => {
    const params = new URLSearchParams()
    params.set('originLat', String(driver.lat))
    params.set('originLng', String(driver.lng))
    if (driver.city) params.set('originCity', driver.city)
    const stateCode = getStateCode(driver.state)
    if (stateCode) params.set('originState', stateCode)
    if (driver.equipment) params.set('equipment', driver.equipment)
    if (driver.name) params.set('driverName', driver.name)
    window.open(`/dispatcher/box-truck-loadboard?${params.toString()}`, '_blank', 'noopener,noreferrer')
}

export const BoxTruckMapPage = () => {
    const navigate = useNavigate()
    const { drivers, loading, error, refresh } = useBoxTruckMap()
    const { loadsByState, loadingStateCodes, failedStateCodes, loadStateCounts } = useBoxTruckMapStateLoads()
    const [selectedState, setSelectedState] = useState<string | null>(null)
    const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null)
    const [stateFocusRequest, setStateFocusRequest] = useState<StateFocusRequest | null>(null)
    const [focusRequest, setFocusRequest] = useState<DriverFocusRequest | null>(null)
    const [clusterInspection, setClusterInspection] = useState<DriverClusterInspection | null>(null)
    const [activeLoadStateCode, setActiveLoadStateCode] = useState<string | null>(null)
    const [resetRequest, setResetRequest] = useState(0)
    const [mapLoadError, setMapLoadError] = useState<string | null>(null)
    const focusRequestId = useRef(0)
    const stateFocusRequestId = useRef(0)
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''
    const visibleLoadsByState = useMemo(
        () => (activeLoadStateCode ? loadsByState.filter((row) => row.state === activeLoadStateCode) : []),
        [activeLoadStateCode, loadsByState]
    )
    const states = useMemo(() => buildStateActivities(visibleLoadsByState, drivers), [drivers, visibleLoadsByState])
    const hasDrivers = drivers.length > 0
    const mapError = apiKey ? mapLoadError : 'Google Maps is not configured for this environment.'

    const requestViewportLoads = useCallback(
        (stateCode: string) => {
            const stateName = getStateName(stateCode)
            if (!stateName) return
            setActiveLoadStateCode(stateCode)
            setSelectedState(stateName)
            void loadStateCounts([stateCode])
        },
        [loadStateCounts]
    )

    const clearViewportLoads = useCallback(() => {
        setActiveLoadStateCode(null)
        setSelectedState(null)
    }, [])

    const retryStateLoads = useCallback((stateCode: string) => void loadStateCounts([stateCode], true), [loadStateCounts])

    const selectState = useCallback(
        (stateName: string | null) => {
            setSelectedState(stateName)
            if (!stateName) {
                setActiveLoadStateCode(null)
                return
            }

            const stateCode = getStateCode(stateName)
            setActiveLoadStateCode(stateCode ?? null)
            if (stateCode) void loadStateCounts([stateCode])
            stateFocusRequestId.current += 1
            setStateFocusRequest({ stateName, requestId: stateFocusRequestId.current })
            setSelectedDriverId(null)
            setClusterInspection(null)
        },
        [loadStateCounts]
    )

    const selectAndFocusDriver = useCallback((driver: BoxTruckMapDriver) => {
        focusRequestId.current += 1
        setSelectedDriverId(driver.driverId)
        setSelectedState(null)
        setActiveLoadStateCode(null)
        setClusterInspection(null)
        setFocusRequest({ driverId: driver.driverId, requestId: focusRequestId.current })
    }, [])

    const resetMap = useCallback(() => {
        setSelectedState(null)
        setSelectedDriverId(null)
        setClusterInspection(null)
        setStateFocusRequest(null)
        setFocusRequest(null)
        setActiveLoadStateCode(null)
        setResetRequest((current) => current + 1)
    }, [])

    const handleMapError = useCallback((cause: unknown) => {
        Sentry.captureException(cause)
        setMapLoadError('Google Maps could not be initialized. Reload the page to try again.')
    }, [])

    const refreshMap = useCallback(() => {
        void refresh()
        if (activeLoadStateCode) void loadStateCounts([activeLoadStateCode], true)
    }, [activeLoadStateCode, loadStateCounts, refresh])

    return (
        <div className="flex h-full flex-col bg-background text-foreground">
            <header className="flex items-center justify-between gap-4 border-b border-border px-6 py-3">
                <div>
                    <h1 className="text-base font-semibold">Map</h1>
                    <p className="text-xs text-foreground-muted">Fresh Box Truck drivers first; active loads appear when you select or zoom into a state</p>
                </div>
                <Button variant="secondary" size="sm" loading={loading} onClick={refreshMap} prefix={<RefreshCw size={16} aria-hidden />}>
                    Refresh
                </Button>
            </header>

            <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto p-4 lg:grid-cols-[minmax(0,1fr)_320px]">
                <section className="relative min-h-[560px] overflow-hidden rounded-2xl bg-surface shadow-surface">
                    {apiKey && !mapLoadError ? (
                        <APIProvider apiKey={apiKey} onError={handleMapError}>
                            <BoxTruckMapCanvas
                                states={states}
                                drivers={drivers}
                                selectedState={selectedState}
                                selectedDriverId={selectedDriverId}
                                stateFocusRequest={stateFocusRequest}
                                focusRequest={focusRequest}
                                resetRequest={resetRequest}
                                onSelectState={selectState}
                                onSelectDriver={setSelectedDriverId}
                                onInspectCluster={setClusterInspection}
                                onFindLoads={openDriverFindLoads}
                                onLoadStateChange={requestViewportLoads}
                                onLoadStateClear={clearViewportLoads}
                            />
                        </APIProvider>
                    ) : null}

                    <BoxTruckMapToolbar drivers={drivers} onSelectDriver={selectAndFocusDriver} onReset={resetMap} />
                    <BoxTruckMapLegend showLoads={visibleLoadsByState.length > 0} />

                    {loading && !hasDrivers && !mapError && (
                        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-surface/80 backdrop-blur-sm">
                            <Spinner color="accent" size={24} />
                            <p className="text-sm text-foreground-muted">Loading fresh drivers…</p>
                        </div>
                    )}

                    {mapError && (
                        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-surface/90 px-6 text-center backdrop-blur-sm">
                            <p className="text-sm font-medium text-danger">Couldn’t load Google Maps</p>
                            <p className="mt-1 max-w-sm text-xs text-foreground-muted">{mapError}</p>
                            {apiKey && (
                                <Button variant="outline" size="sm" className="mt-4" onClick={() => window.location.reload()}>
                                    Reload page
                                </Button>
                            )}
                        </div>
                    )}

                    {error && !hasDrivers && !loading && !mapError && (
                        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-surface/90 px-6 text-center backdrop-blur-sm">
                            <p className="text-sm font-medium text-danger">Couldn’t load map drivers</p>
                            <p className="mt-1 max-w-sm text-xs text-foreground-muted">{error}</p>
                            <Button variant="outline" size="sm" className="mt-4" onClick={refreshMap}>
                                Retry
                            </Button>
                        </div>
                    )}

                    {error && hasDrivers && (
                        <div className="absolute bottom-16 right-4 z-10 rounded-xl bg-danger-soft px-3 py-2 text-xs text-danger-soft-foreground shadow-overlay">
                            Refresh failed. Showing the previous map data.
                        </div>
                    )}
                </section>

                <BoxTruckMapActivityPanel
                    states={states}
                    selectedState={selectedState}
                    selectedDriverId={selectedDriverId}
                    inspection={clusterInspection}
                    loadingStateCodes={loadingStateCodes}
                    failedStateCodes={failedStateCodes}
                    onSelectState={selectState}
                    onSelectDriver={selectAndFocusDriver}
                    onCloseInspection={() => setClusterInspection(null)}
                    onViewStateLoads={(stateCode) => navigate(`/dispatcher/box-truck-loadboard?originState=${stateCode}`)}
                    onRetryStateLoads={retryStateLoads}
                />
            </div>
        </div>
    )
}
